import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check } from 'lucide-react';
import { EmptyState } from '@/components/ui';
import { api } from '@/lib/api-client';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { formatDateTime } from '@/lib/date-format';

interface NotificationItem {
  id: string;
  type: string;
  title: string;
  body: string | null;
  data: Record<string, unknown> | null;
  readAt: string | null;
  createdAt: string;
}

const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL || '/api';
/**
 * NOTIF-05: fallback poll interval used ONLY while the SSE connection is
 * down (initial connect, or after a drop, before the browser's native
 * EventSource retry succeeds). This is not the steady-state behavior
 * anymore — normal operation is push-driven via `/notifications/stream`.
 */
const FALLBACK_POLL_MS = 30000;

export function NotificationBell() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const [open, setOpen] = React.useState(false);
  const [notifications, setNotifications] = React.useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const ref = React.useRef<HTMLDivElement>(null);
  const requestRef = React.useRef<AbortController | null>(null);
  const knownNotificationIdsRef = React.useRef(new Set<string>());
  const notificationsRef = React.useRef<NotificationItem[]>([]);
  const unreadCountRef = React.useRef(0);
  const realtimeVersionRef = React.useRef(0);

  const loadNotifications = React.useCallback(async () => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    const requestVersion = realtimeVersionRef.current;
    try {
      const response = await api.get<{ data: NotificationItem[]; unreadCount: number }>('/notifications', {
        params: { limit: 10 },
        signal: controller.signal,
      });
      if (!controller.signal.aborted) {
        const snapshotIds = new Set(response.data.map((item) => item.id));
        const realtimeEventArrived = realtimeVersionRef.current !== requestVersion;
        const merged = realtimeEventArrived
          ? [...notificationsRef.current.filter((item) => !snapshotIds.has(item.id)), ...response.data]
            .sort((left, right) => Date.parse(right.createdAt) - Date.parse(left.createdAt))
            .slice(0, 10)
          : response.data;
        const extraUnread = realtimeEventArrived
          ? notificationsRef.current.filter((item) => !snapshotIds.has(item.id) && !item.readAt).length
          : 0;
        const nextUnreadCount = response.unreadCount + extraUnread;
        response.data.forEach((item) => knownNotificationIdsRef.current.add(item.id));
        notificationsRef.current = merged;
        unreadCountRef.current = nextUnreadCount;
        setNotifications(merged);
        setUnreadCount(nextUnreadCount);
      }
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        // A transient reconcile error should not interrupt the rest of the shell.
      }
    }
  }, []);

  // NOTIF-05: prepend a just-pushed notification instead of re-fetching the
  // list — keeps the bell responsive to a single event without a network
  // round-trip. Deduplicated by id in case the SSE stream double-delivers
  // (see the comment on publishNotification's redelivery behavior).
  const handlePushedNotification = React.useCallback((item: NotificationItem) => {
    if (knownNotificationIdsRef.current.has(item.id)) return;
    knownNotificationIdsRef.current.add(item.id);
    realtimeVersionRef.current += 1;
    const nextNotifications = [item, ...notificationsRef.current].slice(0, 10);
    notificationsRef.current = nextNotifications;
    setNotifications(nextNotifications);
    if (!item.readAt) {
      const nextUnreadCount = unreadCountRef.current + 1;
      unreadCountRef.current = nextUnreadCount;
      setUnreadCount(nextUnreadCount);
    }
  }, []);

  React.useEffect(() => {
    let pollInterval: ReturnType<typeof setInterval> | undefined;
    let source: EventSource | undefined;
    let disposed = false;

    const startFallbackPolling = () => {
      if (pollInterval) return;
      void loadNotifications();
      pollInterval = setInterval(() => void loadNotifications(), FALLBACK_POLL_MS);
    };
    const stopFallbackPolling = () => {
      if (pollInterval) clearInterval(pollInterval);
      pollInterval = undefined;
    };

    const connect = () => {
      if (disposed || document.visibilityState === 'hidden') return;
      source = new EventSource(`${API_BASE_URL}/notifications/stream`, { withCredentials: true });
      // Reconciles with the REST list immediately (SSE only delivers events
      // created while connected, so a fresh connection needs the current
      // snapshot too) and keeps polling as a safety net until 'open' fires
      // — the browser's built-in EventSource auto-reconnect covers drops
      // after that, but we don't know up front if/when this connect succeeds.
      startFallbackPolling();
      source.addEventListener('open', () => stopFallbackPolling());
      source.addEventListener('notification', (event) => {
        try {
          handlePushedNotification(JSON.parse((event as MessageEvent).data));
        } catch {
          // Malformed payload — ignore rather than crash the bell.
        }
      });
      source.onerror = () => {
        // EventSource retries the connection itself; keep polling as a
        // safety net until it reports 'open' again.
        startFallbackPolling();
      };
    };

    const disconnect = () => {
      source?.close();
      source = undefined;
      stopFallbackPolling();
    };

    const syncVisibility = () => {
      if (document.visibilityState === 'hidden') disconnect();
      else connect();
    };

    syncVisibility();
    document.addEventListener('visibilitychange', syncVisibility);
    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', syncVisibility);
      disconnect();
      requestRef.current?.abort();
    };
  }, [loadNotifications, handlePushedNotification]);

  React.useEffect(() => {
    function handleClick(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  async function markRead(id: string) {
    await api.put(`/notifications/${id}/read`);
    const nextNotifications = notificationsRef.current.map((item) => item.id === id ? { ...item, readAt: new Date().toISOString() } : item);
    notificationsRef.current = nextNotifications;
    setNotifications(nextNotifications);
    const nextUnreadCount = Math.max(0, unreadCountRef.current - 1);
    unreadCountRef.current = nextUnreadCount;
    setUnreadCount(nextUnreadCount);
  }

  function handleItemClick(item: NotificationItem) {
    if (!item.readAt) void markRead(item.id);
    const ticketId = item.data?.ticketId as string | undefined;
    if (ticketId) navigate(`/tickets/${ticketId}`);
    setOpen(false);
  }

  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen((value) => !value)} className="relative rounded-md p-2 text-ink-subtle hover:bg-surface-subtle" aria-label={`${t('shell.notifications')}${unreadCount > 0 ? ` (${t('shell.unread', { count: unreadCount })})` : ''}`} aria-expanded={open}>
        <Bell className="h-5 w-5" aria-hidden="true" />
        {unreadCount > 0 && <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger-500 px-1 text-[10px] font-bold text-white">{unreadCount > 99 ? '99+' : unreadCount}</span>}
      </button>

      {open && (
        <div className="absolute right-0 top-full z-dropdown mt-2 w-80 rounded-lg border bg-surface shadow-raised" role="region" aria-label={t('shell.notifications')}>
          <div className="flex items-center justify-between border-b px-4 py-2"><span className="text-sm font-semibold">{t('shell.notifications')}</span>{unreadCount > 0 && <span className="text-xs text-ink-subtle">{t('shell.unread', { count: unreadCount })}</span>}</div>
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <EmptyState
                title={t('shell.caughtUp')}
                description={t('shell.notificationDescription')}
                className="px-4 py-8"
              />
            ) : notifications.map((item) => (
              <div
                key={item.id}
                role="button"
                tabIndex={0}
                onClick={() => handleItemClick(item)}
                onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); handleItemClick(item); } }}
                className={`flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-surface-hover ${!item.readAt ? 'bg-brand-50/50' : ''}`}
              >
                <div className={`mt-1 h-2 w-2 shrink-0 rounded-full ${!item.readAt ? 'bg-brand-500' : 'bg-transparent'}`} aria-hidden="true" />
                <div className="min-w-0 flex-1"><p className="truncate text-sm font-medium text-ink">{item.title}</p>{item.body && <p className="truncate text-xs text-ink-subtle">{item.body}</p>}<p className="mt-0.5 text-[10px] text-ink-subtle">{formatDateTime(item.createdAt, user?.timezone)}</p></div>
                {!item.readAt && <button type="button" onClick={(event) => { event.stopPropagation(); void markRead(item.id); }} className="shrink-0 rounded p-1 text-ink-subtle hover:bg-line" title={t('common.markAsRead')} aria-label={t('common.markAsRead')}><Check className="h-3.5 w-3.5" aria-hidden="true" /></button>}
              </div>
            ))}
          </div>
          {/* NOTIF-06: the dropdown only shows the latest 10 — link out to
              the full history/pagination/filter page. */}
          <button
            type="button"
            onClick={() => { setOpen(false); navigate('/notifications'); }}
            className="block w-full border-t px-4 py-2 text-center text-sm text-brand-700 hover:bg-surface-hover hover:underline"
          >
            {t('shell.viewAllNotifications')}
          </button>
        </div>
      )}
    </div>
  );
}
